import { useState, useCallback } from 'react'
import { PanelLeftOpen, PanelLeftClose, Menu, Video, PanelsTopLeft, Copy, Share2 } from 'lucide-react'
import { toast } from 'sonner'
import { useNavigate } from 'react-router-dom'
import EditableTitle from '@/components/EditableTitle'
import { useTranslation } from '@/i18n'
import { useAppStore } from '@/store'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { BOARD_LEFTNAV_ID } from './BoardLeftNavRail'
import BoardFileSearch from './BoardFileSearch'
import { isFeatureAllowedInEmbed } from '@/lib/embedFeatures'

interface BoardTopBarV2Props {
  boardName: string
  boardId?: string
  onRename?: (newName: string) => void
  onSidebarToggle?: () => void
  /** Whether the board leftnav is currently expanded — flips the toggle icon. */
  sidebarExpanded?: boolean
  onCallAiEngineer?: () => void
  /** FS-663: opens the share dialog. Omit to hide the Share button entirely. */
  onShare?: () => void
  /** Width of the chat panel in px — used to shift the Call AI Engineer button left */
  chatPanelWidth?: number
  /** Height of the chat panel in px — used to detect overlap with top bar */
  chatPanelHeight?: number
  /** Whether the chat panel is currently open */
  chatOpen?: boolean
}

export default function BoardTopBarV2({
  boardName,
  boardId,
  onRename,
  onSidebarToggle,
  sidebarExpanded = false,
  onCallAiEngineer,
  onShare,
  chatPanelWidth = 0,
  chatPanelHeight = 0,
  chatOpen = false,
}: BoardTopBarV2Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [boardsOpen, setBoardsOpen] = useState(false)
  // FS-573: the hamburger lists BOARDS, not chat history — chat history already
  // lives in the chat panel's own title dropdown, and showing it twice was
  // confusing. Refresh the list each time the menu opens so names/ordering are
  // current.
  const boardList = useAppStore((s) => s.boardList)
  const loadBoardList = useAppStore((s) => s.loadBoardList)
  // The file search docks here rather than floating over the canvas, so it sits
  // on the same line as the board name / Share / Call AI Engineer.
  const isBoardFileSearchOpen = useAppStore((s) => s.isBoardFileSearchOpen)
  const setBoardFileSearchOpen = useAppStore((s) => s.setBoardFileSearchOpen)

  // Greptile P2: without a loading flag, a quick first open (empty store)
  // flashes "No boards yet" until the fetch resolves. A non-empty (possibly
  // stale) list renders immediately and just refreshes in place.
  const [boardsLoading, setBoardsLoading] = useState(false)

  const handleMenuOpenChange = useCallback((open: boolean) => {
    setBoardsOpen(open)
    if (open) {
      setBoardsLoading(true)
      void loadBoardList().finally(() => setBoardsLoading(false))
    }
  }, [loadBoardList])

  const handleSelectBoard = useCallback((id: string) => {
    setBoardsOpen(false)
    if (id !== boardId) navigate(`/board/${id}`)
  }, [boardId, navigate])

  // Determine if the chat panel is tall enough to overlap with the top bar.
  // Top bar bottom edge ≈ 16px padding + 52px height = 68px from top.
  // Chat panel top edge = viewport height - chatPanelHeight - 12px (bottom-3 offset).
  // Overlap occurs when the chat panel's top edge reaches the top bar's bottom.
  // Use a generous threshold (100px) so the button moves slightly before actual overlap.
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900
  const chatOverlapsTopBar = chatOpen && chatPanelHeight >= viewportHeight - 100

  return (
    <div
      className="absolute top-0 left-0 z-20 flex items-center justify-between pt-4 px-4 pointer-events-none transition-[right] duration-300"
      style={{ right: chatOverlapsTopBar ? chatPanelWidth + 12 : 0 }}
    >
      {/* Left: sidebar toggle + board name */}
      <div className="flex gap-3 items-center pointer-events-auto">
        <button
          type="button"
          onClick={onSidebarToggle}
          className="bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_1px_3px_rgba(15,23,42,0.06)] h-12 w-12 flex items-center justify-center hover:bg-[#fafafa] dark:hover:bg-dark-muted transition-colors"
          aria-label={
            sidebarExpanded
              ? t('board:actions.collapse_sidebar', { defaultValue: 'Collapse sidebar' })
              : t('board:actions.open_sidebar', { defaultValue: 'Open sidebar' })
          }
          aria-expanded={sidebarExpanded}
          aria-controls={BOARD_LEFTNAV_ID}
        >
          {/* Single toggle: the icon flips to its opposite when the rail is
              open, so the same control both expands and collapses it. */}
          {sidebarExpanded ? (
            <PanelLeftClose className="w-5 h-5 text-foreground" />
          ) : (
            <PanelLeftOpen className="w-5 h-5 text-foreground" />
          )}
        </button>

        <Popover open={boardsOpen} onOpenChange={handleMenuOpenChange}>
          <div className="group bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_1px_3px_rgba(15,23,42,0.06)] h-12 flex items-center gap-2.5 pl-3 pr-4 min-w-0 max-w-[min(100vw-200px,420px)]">
            <PopoverTrigger asChild>
              <button
                type="button"
                className="flex-shrink-0 hover:opacity-70 transition-opacity"
                aria-label={t('board:recent_boards_text', { defaultValue: 'Recent Boards' })}
              >
                <Menu className="w-5 h-5 text-muted-foreground" />
              </button>
            </PopoverTrigger>
            <div className="h-5 w-px bg-[#e8eaee] dark:bg-dark-border shrink-0" />
            {boardId ? (
              // `!`-prefixed sizes/weights override EditableTitle's internal
              // `font-medium text-lg` defaults — without `!important` the
              // Tailwind-generated `font-medium` + `text-lg` win the CSS
              // specificity tie because they appear later in the bundle,
              // and the board name renders larger / lighter than the
              // matching Call AI Engineer button on the right.
              <EditableTitle
                title={boardName}
                onSave={(newName) => onRename?.(newName)}
                className="!text-[15px] !font-semibold text-foreground truncate min-w-0"
              />
            ) : (
              <span className="text-[15px] font-semibold text-foreground truncate min-w-0">
                {boardName}
              </span>
            )}
            {boardName && (
              <button
                type="button"
                onClick={() => {
                  void navigator.clipboard.writeText(boardName)
                  toast.success(
                    t('board:toasts.board_name_copied', { defaultValue: 'Board name copied' }),
                  )
                }}
                className="shrink-0 opacity-0 group-hover:opacity-50 hover:!opacity-100 transition-opacity"
                title={t('board:actions.copy_board_name', { defaultValue: 'Copy board name' })}
                aria-label={t('board:actions.copy_board_name', { defaultValue: 'Copy board name' })}
              >
                <Copy className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={8}
            className="!w-[380px] !max-h-[480px] !p-0 !gap-0 !bg-white dark:!bg-dark-card !border !border-[#e5e5e5] dark:!border-dark-border !shadow-lg !rounded-xl overflow-hidden"
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-2 border-b border-[#e5e5e5] dark:border-dark-border bg-white dark:bg-dark-card">
              <PanelsTopLeft className="w-4 h-4 text-gray-600 dark:text-gray-300" />
              <span className="text-[13px] font-semibold text-gray-900 dark:text-gray-100">
                {t('board:recent_boards_text', { defaultValue: 'Recent Boards' })}
              </span>
              <span className="ml-auto text-xs text-gray-500 dark:text-gray-400">
                {boardList.length}
              </span>
            </div>

            {/* Board list — most recently viewed first (same order as the Boards page) */}
            <div className="flex flex-col overflow-y-auto bg-white dark:bg-dark-card max-h-[420px]">
              {boardList.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-8 px-4 text-center">
                  {boardsLoading
                    ? t('board:boards_menu.loading', { defaultValue: 'Loading boards…' })
                    : t('board:boards_menu.empty', { defaultValue: 'No boards yet' })}
                </p>
              ) : (
                [...boardList]
                  .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime())
                  .map((board) => {
                    const label = board.name?.trim() || t('board:boards_menu.untitled', { defaultValue: 'Untitled board' })
                    const updated = new Date(board.updated_at)
                    const diffMin = Math.floor((Date.now() - updated.getTime()) / 60000)
                    const diffHr = Math.floor(diffMin / 60)
                    const diffDay = Math.floor(diffHr / 24)
                    const subtitle =
                      diffMin < 1 ? t('board:chat_history.just_now', { defaultValue: 'just now' })
                        : diffMin < 60 ? t('board:chat_history.minutes_ago', { defaultValue: '{{count}}m ago', count: diffMin })
                          : diffHr < 24 ? t('board:chat_history.hours_ago', { defaultValue: '{{count}}h ago', count: diffHr })
                            : diffDay < 7 ? t('board:chat_history.days_ago', { defaultValue: '{{count}}d ago', count: diffDay })
                              : updated.toLocaleDateString()
                    const isActive = board.id === boardId
                    return (
                      <button
                        key={board.id}
                        type="button"
                        onClick={() => handleSelectBoard(board.id)}
                        title={label}
                        className={`flex flex-col items-start gap-0.5 w-full text-left px-4 py-2.5 border-b border-[#f0f0f0] dark:border-dark-border/50 last:border-b-0 transition-colors ${
                          isActive
                            ? 'bg-sky-50 dark:bg-sky-900/30'
                            : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'
                        }`}
                      >
                        <span
                          className={`block w-full overflow-hidden text-ellipsis whitespace-nowrap text-sm leading-5 ${
                            isActive
                              ? 'font-semibold text-sky-900 dark:text-sky-200'
                              : 'font-medium text-gray-900 dark:text-gray-100'
                          }`}
                        >
                          {label}
                        </span>
                        <span className="text-xs text-gray-500 dark:text-gray-400">{subtitle}</span>
                      </button>
                    )
                  })
              )}
            </div>
          </PopoverContent>
        </Popover>
      </div>

      {/* Middle: board file search. The flex-1 spacer is rendered whether or
          not the search is open, so opening it never shifts the board-name card
          or the buttons on the right — only this slot fills. */}
      <div className="flex-1 min-w-0 flex justify-center px-3">
        {isBoardFileSearchOpen && (
          <BoardFileSearch
            className="pointer-events-auto h-12 w-[340px] max-w-full rounded-xl border-[#e8eaee] dark:border-dark-border shadow-[0_1px_3px_rgba(15,23,42,0.06)] px-4 py-0"
            onClose={() => setBoardFileSearchOpen(false)}
          />
        )}
      </div>

      {/* Right: Share + Call AI Engineer */}
      <div className="flex items-center gap-2 pointer-events-auto">
        {/*
          FS-663. Before this the board had NO share entry point at all —
          `ShareModal` was rendered but its open-setter was never called, so the
          dialog was unreachable. Gated by the same embed feature as the modal.
        */}
        {onShare && isFeatureAllowedInEmbed('shareModal') && (
          <button
            type="button"
            onClick={onShare}
            aria-label={t('board:actions.share', { defaultValue: 'Share' })}
            className="bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_1px_3px_rgba(15,23,42,0.06)] h-12 flex items-center gap-2 px-4 hover:bg-[#fafafa] dark:hover:bg-dark-muted transition-colors"
          >
            <Share2 className="w-5 h-5 text-[#0e7490]" />
            <span className="text-[15px] font-semibold text-foreground whitespace-nowrap">
              {t('board:actions.share', { defaultValue: 'Share' })}
            </span>
          </button>
        )}
        {onCallAiEngineer && (
          <button
            type="button"
            onClick={onCallAiEngineer}
            className="bg-white dark:bg-dark-card border border-[#e8eaee] dark:border-dark-border rounded-xl shadow-[0_1px_3px_rgba(15,23,42,0.06)] h-12 flex items-center gap-2 px-4 hover:bg-[#fafafa] dark:hover:bg-dark-muted transition-colors"
          >
            <Video className="w-5 h-5 text-[#0e7490]" />
            <span className="text-[15px] font-semibold text-foreground whitespace-nowrap">
              {t('board:actions.call_ai_engineer', { defaultValue: 'Call AI Engineer' })}
            </span>
          </button>
        )}
      </div>
    </div>
  )
}
